<template>
    <div class="unified-lecture-list-item">
        <v-list-item
            @click="lectureAction()"
            v-ripple="phoneOnly"
            class="unified-lecture-item py-2"
            :class="{
                'unified-lecture-item--phone': phoneOnly,
                'unified-lecture-item--expired': lecture.expired && phoneOnly,
            }"
            :disabled="locked"
            :style="locked ? 'opacity: 0.55' : ''"
        >
            <div class="unified-lecture-item__thumb me-5" :class="{'unified-lecture-item__thumb--fallback': !lecture.image_url}">
                <v-img
                    v-if="lecture.image_url"
                    :src="lecture.image_url"
                    cover
                    :aspect-ratio="16/10"
                    class="unified-lecture-item__img"
                    :class="{'unified-lecture-item__img--seen': lecture.seen}"
                />
                <v-sheet v-else :color="color" dark class="unified-lecture-item__fallback">
                    <v-progress-circular v-if="loading" color="white" indeterminate :size="22" :width="3" style="opacity: 0.7" />
                    <v-icon v-else color="white" :size="phoneOnly ? 28 : 32">{{ fallbackIcon }}</v-icon>
                </v-sheet>
                <v-chip v-if="lecture.seen && lecture.image_url" x-small color="success" class="unified-lecture-item__seen-badge px-1">
                    <v-icon size="13" color="white">mdi-check-bold</v-icon>
                </v-chip>
            </div>

            <v-list-item-content class="unified-lecture-item__content align-self-start py-2">
                <v-list-item-title class="strong-text--text unified-lecture-item__title mb-1">
                    {{ lecture.name }}
                </v-list-item-title>

                <v-list-item-subtitle v-if="lecture.description" class="mb-2">
                    <div class="details-text text--text unified-lecture-item__description">{{ lecture.description }}</div>
                </v-list-item-subtitle>

                <div class="unified-lecture-item__meta d-flex flex-wrap align-center mb-1">
                    <v-chip v-if="lecture.videos_count" x-small label outlined class="me-1 mb-1 px-2">
                        <v-icon x-small left class="me-1">mdi-play-box-multiple-outline</v-icon>
                        {{ unitFormat(lecture.videos_count, 'محاضرة', 'محاضرتين', 'محاضرات') }}
                    </v-chip>
                    <v-chip v-if="lecture.seen" x-small label outlined color="success darken-1" class="me-1 mb-1 px-2">
                        <v-icon x-small left class="me-1">mdi-check-circle-outline</v-icon>
                        مكتملة
                    </v-chip>
                    <v-chip v-if="lecture.preview && pay_type === 'course'" x-small label outlined color="deep-purple accent-4" class="me-1 mb-1 px-2">
                        <v-icon x-small left class="me-1">mdi-play-speed</v-icon>
                        معاينة
                    </v-chip>
                    <v-chip v-if="lecture.expired" x-small label outlined color="error" class="me-1 mb-1 px-2">
                        منتهية
                    </v-chip>
                </div>

                <v-list-item-subtitle class="unified-lecture-item__status details-text text--text" style="font-size: 11px!important;">
                    <template v-if="pay_type === 'course'">
                        <span v-if="lecture.expired" class="error--text">منتهية الصلاحية</span>
                        <span v-else-if="lecture.purchased">اضغط لعرض محتويات المحاضرة</span>
                        <span v-else-if="lecture.preview">اضغط لمعاينة المحاضرة</span>
                        <span v-else class="muted--text">احصل على الدورة لعرض المحاضرة</span>
                    </template>
                    <template v-else>
                        <template v-if="!lecture.purchased">
                            <span v-if="free" class="deep-purple--text text--accent-4">احصل عليها الآن</span>
                            <span v-else class="strong-text--text font-weight-medium details-text">{{ moneyFormat(lecture.price, true) }}</span>
                        </template>
                        <span v-else-if="lecture.expired" class="error--text">منتهية الصلاحية</span>
                        <span v-else-if="free">اضغط لعرض المحاضرة</span>
                        <span v-else>
                            تم الشراء
                            <template v-if="lecture.repurchasable">
                                ·
                                <v-btn color="strong-text" text x-small class="px-1 unified-lecture-item__repurchase" @click.stop="purchaseDialog = true" @touchstart.stop @mousedown.stop>
                                    {{ free ? 'احصل عليها' : 'شراء' }} مرة أخرى
                                </v-btn>
                            </template>
                        </span>
                    </template>
                </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-icon class="unified-lecture-item__action ms-2">
                <v-icon :color="actionIconColor">{{ actionIcon }}</v-icon>
            </v-list-item-icon>
        </v-list-item>

        <div class="unified-lecture-item__progress">
            <v-progress-linear v-if="loading" :active="loading" indeterminate height="2" color="primary" />
        </div>

        <lecture-purchase-dialog v-if="pay_type !== 'course'" v-model="purchaseDialog" :lecture="lecture" :free="free" @purchased="sync" />
        <lecture-dialog v-if="hasAccess" v-model="videosDialog" :pay_type="pay_type" :lecture="lectureData || lecture" :items="items" :attachments="attachments" />
    </div>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'
import LecturePurchaseDialog from '@/components/custom/lecture-purchase-dialog'
import LectureDialog from './lecture-dialog'

export default {
    props: {
        lecture: {},
        pay_type: {},
    },
    components: { LecturePurchaseDialog, LectureDialog },
    data(){
        return {
            purchaseDialog: false,
            videosDialog: false,
            lectureData: null,
            items: null,
            attachments: null,
            loading: false,
        }
    },
    computed: {
        ...GlobalComputed,
        free(){
            return this.lecture.free || this.lecture.price === 0;
        },
        locked(){
            return this.pay_type === 'course' && !this.lecture.preview && !this.lecture.purchased;
        },
        hasAccess(){
            if (this.pay_type === 'course'){
                return this.lecture.purchased || this.lecture.preview;
            }
            return this.lecture.purchased;
        },
        color(){
            if (this.pay_type === 'course'){
                if (this.locked) return 'strong-text';
                if (this.lecture.preview) return 'deep-purple accent-4';
                return 'primary';
            }
            return this.lecture.purchased ? 'secondary' : 'primary';
        },
        fallbackIcon(){
            if (this.pay_type === 'course'){
                if (this.lecture.purchased) return 'mdi-play-circle';
                if (this.lecture.preview) return 'mdi-play-speed';
                return 'mdi-play-circle-outline';
            }
            return this.lecture.purchased ? 'mdi-motion-play' : 'mdi-clipboard-play-outline';
        },
        actionIcon(){
            if (this.locked) return 'mdi-shield-lock-outline';
            if (this.pay_type === 'course' && this.lecture.preview && !this.lecture.purchased) return 'mdi-shield-lock-open-outline';
            if (!this.hasAccess) {
                if (this.free) return 'mdi-star-four-points-outline';
                return 'mdi-credit-card-plus-outline';
            }
            return 'mdi-chevron-left';
        },
        actionIconColor(){
            if (this.locked) return '';
            if (this.pay_type === 'course' && this.lecture.preview && !this.lecture.purchased) return 'deep-purple accent-4';
            if (!this.hasAccess && this.free) return 'deep-purple accent-4';
            return 'primary';
        },
    },
    methods: {
        ...GlobalMethods,
        sync(lecture){
            this.$emit('sync', lecture);
        },
        lectureAction(){
            if (this.hasAccess){
                if (this.appData.lectures_view_layout === 'seperated'){
                    this.$router.push({ name: 'lecture-unified', params: { id: this.lecture.id } });
                }else{
                    this.showVideos();
                }
            }else{
                this.purchaseDialog = true;
            }
        },
        showVideos(){
            this.loadLectureContents();
        },
        loadLectureContents(){
            if (this.loading) return;
            this.loading = true;
            window.axios.get(`/lectures/${this.lecture.id}/contents/unified`).then(response=>{
                this.loading = false;
                this.lectureData = response.data.lecture;
                this.items = response.data.items;
                this.attachments = response.data.attachments;
                this.videosDialog = true;
            }).catch(()=>{
                this.loading = false;
                this.showMsg('حدثت مشكلة أثناء جلب الفيديوهات', 'error');
            });
        },
    },
}
</script>

<style scoped>
.unified-lecture-item {
    align-items: flex-start !important;
    min-height: 0;
}

.unified-lecture-item--phone {
    padding-inline: 16px !important;
}

.unified-lecture-item--expired {
    background: rgba(0, 0, 0, 0.03);
}

.unified-lecture-item__thumb {
    position: relative;
    flex-shrink: 0;
    width: 112px;
    align-self: center;
    border-radius: 8px;
    overflow: hidden;
}

.unified-lecture-item__img {
    border-radius: 8px;
}

.unified-lecture-item__img--seen {
    opacity: 0.82;
}

.unified-lecture-item__fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    aspect-ratio: 16 / 10;
    border-radius: 8px !important;
}

.unified-lecture-item__seen-badge {
    position: absolute;
    bottom: 6px;
    left: 6px;
    height: 20px !important;
    min-width: 20px !important;
    pointer-events: none;
}

.unified-lecture-item__content {
    min-width: 0;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
}

.unified-lecture-item__title {
    line-height: 1.35 !important;
    white-space: normal !important;
    word-break: break-word;
}

.unified-lecture-item__description {
    line-height: 1.5;
    text-align: start;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.unified-lecture-item__meta {
    gap: 0;
}

.unified-lecture-item__status {
    line-height: 1.45 !important;
    white-space: normal !important;
}

.unified-lecture-item__action {
    min-width: 28px !important;
}

.unified-lecture-item__repurchase {
    height: auto !important;
    min-width: 0 !important;
    vertical-align: baseline;
}

.unified-lecture-item__progress {
    height: 2px;
}

@media (min-width: 960px) {
    .unified-lecture-item {
        padding-inline: 4px !important;
    }

    .unified-lecture-item__thumb {
        width: 132px;
    }

    .unified-lecture-item__title {
        font-size: 1rem;
    }
}
</style>
